import { ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ConversationMessageContentType } from 'src/@types/conversation';
import Iconify from 'src/components/iconify';
import MenuPopover from 'src/components/menu-popover';
import { useLocales } from 'src/locales';
import { checkFileType } from '../utils/checkFileType';
import { StyledInputButton } from './styles';

type Props = {
  disabled: boolean | undefined;
  onSelectFile: (type: ConversationMessageContentType, file: File) => void;
  iconSize?: number;
};

export const MediaInputActions = ({
  disabled,
  onSelectFile,
  iconSize = 24,
}: Props) => {
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const theme = useTheme();
  const { translate } = useLocales();

  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClickAttachImage = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const handleClickAttachFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageVideoSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const type = checkFileType(file.type);
    handleFileSelect(event, type);
  };

  const handleOpenModal = (
    type: ConversationMessageContentType,
    file: File,
  ) => {
    onSelectFile(type, file);
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: ConversationMessageContentType,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    handleOpenModal(type, file);
    event.target.value = '';
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const options = useMemo(
    () => [
      {
        title: translate('shared.labels.photos_and_videos'),
        icon: 'tabler:photo-video',
        action: handleClickAttachImage,
      },
      {
        title: translate('shared.labels.documents'),
        icon: 'tabler:file-text',
        action: handleClickAttachFile,
      },
    ],
    [handleClickAttachImage, handleClickAttachFile, translate],
  );

  return (
    <>
      <Tooltip title={translate('shared.labels.add_files')} arrow>
        <StyledInputButton
          onClick={handleOpenPopover}
          disabled={disabled}
          size="small"
          sx={{
            width: iconSize,
            height: iconSize,
          }}
        >
          <Iconify icon="tabler:paperclip" />
        </StyledInputButton>
      </Tooltip>
      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 320, marginTop: -0.75, marginLeft: -1.5 }}
        arrow="bottom-left"
      >
        {options.map((item, index) => (
          <MenuItem key={index} onClick={item.action}>
            <ListItemIcon>
              <Iconify icon={item.icon} color={theme.palette.text.secondary} />
            </ListItemIcon>
            <ListItemText primary={item.title} />
          </MenuItem>
        ))}
      </MenuPopover>
      <input
        type="file"
        ref={imageInputRef}
        style={{ display: 'none' }}
        accept="image/*,video/*"
        onChange={(evt) => handleImageVideoSelect(evt)}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(evt) =>
          handleFileSelect(evt, ConversationMessageContentType.FILE)
        }
      />
    </>
  );
};
